import {
	CrownOutlined,
	ExperimentOutlined,
	ReadOutlined,
	RiseOutlined,
	TeamOutlined,
	UserOutlined,
} from '@ant-design/icons';
import { Card, Col, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import type { DashboardStats } from '@/types';

interface StatisticsCardsProps {
	stats: DashboardStats;
}

const { Text } = Typography;

interface StatCardProps {
	title: string;
	value: string | number;
	subtitle?: React.ReactNode;
	icon?: React.ReactNode;
}

const StatCard = ({ title, value, subtitle, icon }: StatCardProps) => {
	return (
		<Card className='h-full border border-slate-100 shadow-sm rounded-xl hover:shadow-md transition-shadow duration-300'>
			<div className='flex justify-between items-start mb-2'>
				<Text className='text-slate-500 font-medium text-sm'>{title}</Text>
				{icon && <div className='p-2 bg-slate-50 rounded-lg text-slate-400'>{icon}</div>}
			</div>
			<div className='space-y-1'>
				<div className='text-2xl font-bold text-slate-800 tracking-tight'>{value}</div>
				{subtitle && <div className='text-xs text-slate-400'>{subtitle}</div>}
			</div>
		</Card>
	);
};

const StatisticsCards = ({ stats }: StatisticsCardsProps) => {
	const { t } = useTranslation('components');

	return (
		<Row gutter={[16, 16]}>
			<Col xs={24} sm={12} lg={6}>
				<StatCard
					title={t('dashboard.stats.petOwners')}
					value={stats.totalUsers}
					icon={<UserOutlined />}
					subtitle={
						<span className='flex items-center gap-1 text-emerald-600'>
							<RiseOutlined />
							+{stats.newUsersThisWeek} {t('dashboard.stats.thisWeek')}
						</span>
					}
				/>
			</Col>

			<Col xs={24} sm={12} lg={6}>
				<StatCard
					title={t('dashboard.stats.totalPets')}
					value={stats.totalPets}
					icon={<TeamOutlined />}
					subtitle={`+${stats.newPetsThisWeek} ${t('dashboard.stats.thisWeek')}`}
				/>
			</Col>

			<Col xs={24} sm={12} lg={6}>
				<StatCard
					title={t('dashboard.stats.aiScansToday')}
					value={stats.aiScansToday}
					icon={<ExperimentOutlined />}
					subtitle={t('dashboard.stats.aiScansSubtitle')}
				/>
			</Col>

			<Col xs={24} sm={12} lg={6}>
				<StatCard
					title={t('dashboard.stats.proSubscribers')}
					value={stats.activeSubscriptions}
					icon={<CrownOutlined />}
					subtitle={t('dashboard.stats.proSubtitle')}
				/>
			</Col>

			<Col xs={24} sm={12} lg={6}>
				<StatCard
					title={t('dashboard.stats.breeds')}
					value={stats.totalBreeds}
					icon={<ReadOutlined />}
					subtitle={t('dashboard.stats.breedsSubtitle')}
				/>
			</Col>

			<Col xs={24} sm={12} lg={6}>
				<StatCard
					title={t('dashboard.stats.resources')}
					value={stats.totalResources}
					icon={<ReadOutlined />}
					subtitle={t('dashboard.stats.resourcesSubtitle')}
				/>
			</Col>
		</Row>
	);
};

export { StatisticsCards };
export default StatisticsCards;
